import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import ImgWithSignedUrl from "components/SignedUrl/ImgWithSignedUrl";
import WOPdfButton from "components/Pdfs/WOPdf/WOPdfButton";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * A work order's PRODUCTS, opened from the W-ID on an address group's row.
 *
 * Deliberately products only — no header facts. This is opened while packing
 * against a shipping address the modal behind is already showing, so repeating
 * the address and contact here would just push the products down.
 *
 * Two modals deep (details → products → full image), so each level sets its
 * own z-index: Bootstrap gives every modal the same one, which puts a stacked
 * modal's backdrop UNDERNEATH the modal below it and makes it look like the
 * two are blended together. Learned on 2026-08-27 with the Link WO modal.
 */

export default function WorkOrderProductsModal({ woId, onHide }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Product-type filter, same behaviour as QuickView's: always rendered, never
  // hidden below a threshold — hiding them "when there are only two" is what
  // made them impossible to find on 2026-08-28.
  const [productType, setProductType] = useState(null);
  // The product whose image is open full-size, or null.
  const [zoomed, setZoomed] = useState(null);
  // The FULL work order row, only for the Get Pdf button.
  //
  // /bundle-sync/work-order-detail deliberately returns a trimmed shape for
  // this modal, but WOPdf needs billing address, payment term, sales person,
  // tax rate and the location's pdfLogo. Rather than widen that endpoint (and
  // duplicate seven includes), this reuses the same /work-order/:id row
  // QuickView's PDF is built from, so the two produce an identical document.
  //
  // Null until it arrives, and left null if it fails: the button simply does
  // not render, which is the right outcome -- the products list is what this
  // modal is for and must not be held hostage to the PDF.
  const [pdfWorkOrder, setPdfWorkOrder] = useState(null);

  const load = useCallback(async () => {
    if (!woId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await API.post(
        "/bundle-sync/work-order-detail",
        { woId },
        { headers: authHeader() },
      );
      if (!res?.success) throw new Error(res?.message || "Could not load");
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setData(null);
    }
    setLoading(false);
  }, [woId]);

  // Separate from load() on purpose: a failure here must not blank the
  // products, and the products must not wait on it to render.
  const loadPdfWorkOrder = useCallback(async () => {
    if (!woId) return;
    try {
      const { data: res } = await API.get(`/work-order/${woId}`, {
        headers: authHeader(),
      });
      setPdfWorkOrder(res?.result || null);
    } catch (err) {
      setPdfWorkOrder(null);
    }
  }, [woId]);

  useEffect(() => {
    load();
    loadPdfWorkOrder();
  }, [load, loadPdfWorkOrder]);

  // Reset the filter when a different work order opens, or a type the new one
  // does not have would leave an empty table.
  useEffect(() => {
    setProductType(null);
    setZoomed(null);
    // Or the previous order's PDF stays available under the new order's header.
    setPdfWorkOrder(null);
  }, [woId]);

  const products = data?.products || [];

  // One entry per type, in first-seen order so the buttons keep the order the
  // products are listed in. Counts are of ALL products, so each button says
  // what it would show even while another filter is active.
  const typeCounts = [];
  const typeIndex = new Map();
  for (const p of products) {
    const name = p.productType || "Unspecified";
    if (!typeIndex.has(name)) {
      typeIndex.set(name, typeCounts.length);
      typeCounts.push({ name, count: 0, qty: 0 });
    }
    const t = typeCounts[typeIndex.get(name)];
    t.count += 1;
    t.qty += p.totalQuantity || 0;
  }

  // A filter naming a type this work order no longer has would show an empty
  // table with no way back, so an unknown selection falls through to All.
  const activeType = typeIndex.has(productType) ? productType : null;
  const visible = activeType
    ? products.filter((p) => (p.productType || "Unspecified") === activeType)
    : products;

  return (
    <>
      <style>
        {`
          /* Above the details modal that opens this, which itself sits
             above the sidebar's z-index 99999 -- see the stacking note in
             NovaShipping.jsx. Backdrop directly under its own modal. */
          .ns-products-backdrop { z-index: 100030; }
          .ns-products-modal    { z-index: 100031; }
          .ns-zoom-backdrop     { z-index: 100040; }
          .ns-zoom-modal        { z-index: 100041; }
          /* The Get Pdf options dialog, opened from THIS modal. Its own
             default (100002) is below this modal's 100031, which rendered it
             underneath -- the button looked dead. */
          .ns-pdf-backdrop      { z-index: 100060; }
          /* The thumbnail is the double-click target, so it has to look like
             one. */
          .ns-product-thumb { cursor: zoom-in; }
        `}
      </style>

      <Modal
        show={!!woId}
        onHide={onHide}
        size="xl"
        scrollable
        className="ns-products-modal"
        backdropClassName="ns-products-backdrop"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton>
          {/* Laid out like QuickView's header: a small green View button
              first, then the W-ID as plain text rather than a badge.
              Opens in a NEW TAB, unlike QuickView's in-place navigation --
              this modal sits inside the invoice Details modal, so navigating
              away would discard the invoice, the customer selection and the
              open accordion the user came from. */}
          <Modal.Title className="h5 d-flex align-items-center flex-wrap gap-2 mb-0 flex-grow-1">
            <Link
              to={`/work-order/details/all/${woId}?page=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-success py-0 px-1"
              title={`Open work order W-${woId} in a new tab`}
            >
              View
            </Link>
            <span>W-{woId}</span>
            <span>{data?.customerName || ""}</span>
            {data?.jobName ? (
              <span className="text-muted fs-6">{data.jobName}</span>
            ) : null}
          </Modal.Title>

          {/* Get Pdf, at the far right of the header like QuickView's.
              ms-auto pushes it against the close button: .modal-header is
              space-between, so without it three children would spread and
              leave this floating mid-header.

              No onPrinted handler, unlike QuickView. There it is deliberate --
              staff print from the preview, so generating IS the print. Here
              the modal is opened while PACKING against a shipping address, and
              silently flipping a work order's Printed flag from the shipping
              screen is a side effect nobody asked for. */}
          {pdfWorkOrder ? (
            <div className="me-2">
              <WOPdfButton
                btnClasses="btn btn-sm"
                btnColor="btn-outline-primary"
                selectedWO={pdfWorkOrder}
                woProducts={data?.products}
                zIndex={100061}
                backdropClassName="ns-pdf-backdrop"
              />
            </div>
          ) : null}
        </Modal.Header>

        <Modal.Body>
          {loading && (
            <div className="text-center text-muted py-4">Loading products…</div>
          )}

          {error && (
            <div className="alert alert-danger py-2 px-3 mb-0">
              Could not load W-{woId}: {error}
              <button className="btn btn-sm btn-link" onClick={load}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Product-type tabs, always shown. */}
              <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    activeType === null ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setProductType(null)}
                >
                  All <span className="ms-1 badge bg-light text-dark">{products.length}</span>
                </button>
                {typeCounts.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    className={`btn btn-sm ${
                      activeType === t.name ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => setProductType(t.name)}
                    title={`${t.count} product${t.count === 1 ? "" : "s"}, ${t.qty} pcs`}
                  >
                    {t.name}{" "}
                    <span className="ms-1 badge bg-light text-dark">{t.count}</span>
                  </button>
                ))}
                <span className="ms-auto small text-muted">
                  Total qty <strong>{data.totalQuantity}</strong>
                </span>
              </div>

              {visible.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No products on this work order.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr className="text-muted small text-uppercase">
                        <th style={{ width: 90 }}>Image</th>
                        <th>Type</th>
                        <th>Style</th>
                        <th>Description</th>
                        <th>Color</th>
                        <th>Fabric</th>
                        <th className="text-center" style={{ width: 80 }}>
                          Qty
                        </th>
                        <th className="text-center" style={{ width: 80 }}>
                          Ready
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {p.productImage ? (
                              <div
                                className="ns-product-thumb border rounded overflow-hidden d-flex align-items-center justify-content-center"
                                style={{ width: 64, height: 64 }}
                                onDoubleClick={() => setZoomed(p)}
                                title="Double-click to view full size"
                              >
                                <ImgWithSignedUrl
                                  path={p.productImage}
                                  styles={{
                                    maxWidth: "100%",
                                    maxHeight: 64,
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className="border rounded d-flex align-items-center justify-content-center text-muted"
                                style={{ width: 64, height: 64, fontSize: 10 }}
                              >
                                No image
                              </div>
                            )}
                          </td>
                          <td className="text-nowrap">{p.productType || "-"}</td>
                          <td className="text-nowrap">{p.productStyle || "-"}</td>
                          <td style={{ minWidth: 180 }}>
                            {p.productDescription || "-"}
                            {p.teamName ? (
                              <span className="text-muted small">
                                {" "}
                                · {p.teamName}
                              </span>
                            ) : null}
                          </td>
                          <td className="text-nowrap">{p.productColor || "-"}</td>
                          <td className="text-nowrap">{p.productFabric || "-"}</td>
                          <td className="text-center fw-semibold">
                            {p.totalQuantity || 0}
                          </td>
                          <td className="text-center">
                            {p.isReady ? (
                              <span className="badge bg-success">Yes</span>
                            ) : (
                              <span className="badge bg-secondary">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <span className="small text-muted">
            {products.length} product{products.length === 1 ? "" : "s"}
            {activeType ? ` · showing ${visible.length}` : ""}
          </span>
          <button className="btn btn-secondary" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Full-size image, one level further up the stack. */}
      <Modal
        show={!!zoomed}
        onHide={() => setZoomed(null)}
        size="lg"
        centered
        className="ns-zoom-modal"
        backdropClassName="ns-zoom-backdrop"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            {zoomed?.productStyle || zoomed?.productType || "Product image"}
            {zoomed?.productColor ? (
              <span className="text-muted ms-2">{zoomed.productColor}</span>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {zoomed?.productImage && (
            <ImgWithSignedUrl
              path={zoomed.productImage}
              styles={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
